'use client';

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  CheckCircle,
  Shield,
  Info,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import AuthHeroPanel from './AuthHeroPanel';

// ─── Types ────────────────────────────────────────────────────────────────────

type AuthMode = 'login' | 'signup' | 'forgot';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { score, label: 'Weak', color: 'bg-rose-400' };
  if (score <= 2) return { score, label: 'Fair', color: 'bg-amber-400' };
  if (score <= 3) return { score, label: 'Good', color: 'bg-violet-500' };
  return { score, label: 'Strong', color: 'bg-emerald-500' };
}

function friendlyAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Incorrect email or password. Please check your credentials and try again.';
  }
  if (lower.includes('email not confirmed') || lower.includes('email_not_confirmed')) {
    return 'Please verify your email address before signing in. Check your inbox for a verification link.';
  }
  if (lower.includes('user already registered') || lower.includes('already been registered')) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (lower.includes('password should be at least')) {
    return 'Password must be at least 8 characters long.';
  }
  if (lower.includes('weak password')) {
    return 'Password is too weak. Use a mix of letters, numbers, and symbols.';
  }
  if (lower.includes('rate limit') || lower.includes('too many requests')) {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Connection error. Please check your internet connection and try again.';
  }
  return 'Something went wrong. Please try again.';
}

// ─── Tab reader — isolated so useSearchParams is Suspense-safe ───────────────

function TabReader({ onTab }: { onTab: (tab: string | null) => void }) {
  const searchParams = useSearchParams();
  useEffect(() => {
    onTab(searchParams?.get('tab') ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// ─── Main form component ──────────────────────────────────────────────────────

function AuthFormInner() {
  const router = useRouter();

  // ── State ──
  const [mode, setMode] = useState<AuthMode>('login');
  const [tabResolved, setTabResolved] = useState(false);

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Signup fields
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupTerms, setSignupTerms] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupVerificationSent, setSignupVerificationSent] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Shared
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Rate limit
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  // ── Rate limit countdown ──
  useEffect(() => {
    if (!rateLimitUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
      setRateLimitCountdown(remaining);
      if (remaining === 0) setRateLimitUntil(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil]);

  const isRateLimited = rateLimitUntil !== null && rateLimitCountdown > 0;

  const handleRateLimit = (message: string): boolean => {
    const lower = message.toLowerCase();
    if (lower.includes('rate limit') || lower.includes('too many requests') || lower.includes('429')) {
      const secondsMatch = lower.match(/(\d+)\s*second/);
      const waitSeconds = secondsMatch ? parseInt(secondsMatch[1], 10) : 60;
      setRateLimitUntil(Date.now() + waitSeconds * 1000);
      setRateLimitCountdown(waitSeconds);
      return true;
    }
    return false;
  };

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setLoginError('');
    setSignupError('');
    setForgotError('');
    setRateLimitUntil(null);
    setRateLimitCountdown(0);
  };

  // ── Handle tab from URL ──
  const handleTab = useCallback((tab: string | null) => {
    if (tab === 'signup') setMode('signup');
    else if (tab === 'login') setMode('login');
    setTabResolved(true);
  }, []);

  // ── Login submit ──
  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || isRateLimited) return;

    setLoginError('');
    console.log('[AUTH] LOGIN SUBMIT STARTED', { email: loginEmail, ts: new Date().toISOString() });

    if (!loginEmail.trim() || !loginPassword) {
      setLoginError('Please enter your email and password.');
      return;
    }

    setIsLoading(true);
    try {
      const supabase = createClient();
      console.log('[AUTH] calling supabase.auth.signInWithPassword...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });
      console.log('[AUTH] signInWithPassword COMPLETED', { hasUser: !!data?.user, error: error?.message });

      if (error) throw error;

      console.log('[AUTH] LOGIN SUCCESS — user:', data.user?.id);

      // Fetch profile to determine redirect
      let redirectTarget = '/dashboard';
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, role')
          .eq('id', data.user.id)
          .single();
        if (profile?.role === 'admin' || profile?.role === 'super_admin') {
          redirectTarget = '/admin-dashboard';
        } else if (!profile?.onboarding_completed) {
          redirectTarget = '/onboarding';
        }
      } catch (profileErr) {
        console.warn('[AUTH] profile fetch failed (non-blocking)', profileErr);
      }

      console.log('[AUTH] redirecting to', redirectTarget);
      router.push(redirectTarget);
    } catch (err: unknown) {
      console.error('[AUTH] LOGIN ERROR', err);
      const raw = err instanceof Error ? err.message : 'Login failed';
      const isRL = handleRateLimit(raw);
      if (!isRL) setLoginError(friendlyAuthError(raw));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Signup submit ──
  const handleSignupSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || isRateLimited) return;

    setSignupError('');
    console.log('[AUTH] SIGNUP SUBMIT STARTED', { email: signupEmail, ts: new Date().toISOString() });

    // Client-side validation
    if (!signupName.trim()) { setSignupError('Please enter your full name.'); return; }
    if (!signupEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail)) { setSignupError('Please enter a valid email address.'); return; }
    if (signupPassword.length < 8) { setSignupError('Password must be at least 8 characters.'); return; }
    if (signupPassword !== signupConfirm) { setSignupError('Passwords do not match.'); return; }
    if (!signupTerms) { setSignupError('Please agree to the Terms of Service and Privacy Policy.'); return; }

    setIsLoading(true);
    try {
      const supabase = createClient();
const siteUrl =
  process.env.NEXT_PUBLIC_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://jobcompanion.app');
const redirectTo = `${siteUrl.replace(/\/$/, '')}/auth/callback`;
console.log('[AUTH] redirectTo resolved:', redirectTo);
console.log('[AUTH] calling supabase.auth.signUp...', { email: signupEmail, redirectTo });

      const { data, error } = await supabase.auth.signUp({
        email: signupEmail.trim(),
        password: signupPassword,
        options: {
          data: {
            full_name: signupName.trim(),
            role: 'user',
          },
emailRedirectTo: redirectTo,
        },
      });

      console.log('[AUTH] signUp COMPLETED', { hasUser: !!data?.user, userId: data?.user?.id, error: error?.message });

      if (error) throw error;

      // Supabase returns user=null when email confirmation is required — that's expected
      console.log('[AUTH] SIGNUP SUCCESS — verification email sent to', signupEmail);
      setVerifiedEmail(signupEmail.trim());
      setSignupVerificationSent(true);
    } catch (err: unknown) {
      console.error('[AUTH] SIGNUP ERROR', err);
      const raw = err instanceof Error ? err.message : 'Signup failed';
      const isRL = handleRateLimit(raw);
      if (!isRL) setSignupError(friendlyAuthError(raw));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Forgot password submit ──
  const handleForgotSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || isRateLimited) return;

    setForgotError('');
    if (!forgotEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const supabase = createClient();
const siteUrl =
  process.env.NEXT_PUBLIC_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://jobcompanion.app');
const forgotRedirectTo = `${siteUrl.replace(/\/$/, '')}/auth/callback?next=/dashboard`;
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
redirectTo: forgotRedirectTo,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Failed to send reset email';
      const isRL = handleRateLimit(raw);
      if (!isRL) setForgotError(friendlyAuthError(raw));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend verification ──
  const handleResend = async () => {
    if (!verifiedEmail) return;
    try {
      const supabase = createClient();
      await supabase.auth.resend({ type: 'signup', email: verifiedEmail });
    } catch {
      // silent — user can try again
    }
  };

  const passwordStrength = getPasswordStrength(signupPassword);

  // ── Render ──
  return (
    <div className="min-h-screen flex bg-white">
      {/* Suspense-safe tab reader */}
      <Suspense fallback={null}>
        <TabReader onTab={handleTab} />
      </Suspense>

      {/* Left brand panel */}
      <AuthHeroPanel />

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12 xl:px-14 overflow-y-auto bg-slate-50/50">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={28} />
          <span className="font-semibold text-slate-800">JobCompanion</span>
        </div>

        <div className="w-full max-w-md mx-auto">

          {/* Mode toggle */}
          {mode !== 'forgot' && !signupVerificationSent && (
            <div className="flex bg-slate-100 border border-slate-200 rounded-xl p-1 mb-8">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  mode === 'login' ? 'bg-white text-slate-800 shadow-sm border border-slate-200/80' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => switchMode('signup')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  mode === 'signup' ? 'bg-white text-slate-800 shadow-sm border border-slate-200/80' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Create Account
              </button>
            </div>
          )}

          {/* Rate-limit banner */}
          {isRateLimited && (
            <div className="mb-4 flex items-start gap-3 p-3.5 rounded-lg bg-amber-50 border border-amber-200">
              <Info size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-700">Too many attempts — please wait</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Try again in <span className="font-semibold tabular-nums">{rateLimitCountdown}s</span>
                </p>
              </div>
            </div>
          )}

          {/* Heading */}
          {!signupVerificationSent && (
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-slate-800">
                {mode === 'login' ? 'Welcome back' : mode === 'signup' ? 'Start your AI job search' : 'Reset your password'}
              </h2>
              <p className="text-sm text-slate-500 mt-1.5">
                {mode === 'login' ?'Sign in to access your job pipeline and AI tools'
                  : mode === 'signup' ?'Create your account and upload your resume to get started' :'Enter your email and we will send you a reset link'}
              </p>
            </div>
          )}

          {/* ── Verification sent state ── */}
          {signupVerificationSent && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                <Mail size={26} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-500 mb-1">We sent a verification link to</p>
              <p className="text-sm font-semibold text-slate-800 mb-4">{verifiedEmail}</p>
              <p className="text-xs text-slate-400 mb-6 max-w-xs mx-auto">
                Click the link in the email to verify your account. Check your spam folder if you don&apos;t see it within a few minutes.
              </p>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleResend}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-800 hover:border-slate-300 transition-colors bg-white"
                >
                  <Mail size={14} />
                  Resend verification email
                </button>
                <button
                  type="button"
                  onClick={() => { setSignupVerificationSent(false); switchMode('login'); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-2"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          )}

          {/* ── Login Form ── */}
          {mode === 'login' && !signupVerificationSent && (
            <form onSubmit={handleLoginSubmit} noValidate className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-600">Password</label>
                  <button type="button" onClick={() => switchMode('forgot')} className="text-xs text-violet-600 hover:text-violet-700 transition-colors">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200">
                  <Shield size={13} className="text-rose-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-rose-700">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || isRateLimited}
                className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-violet-200"
                style={{ minHeight: '44px' }}
              >
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /><span>Signing in…</span></>
                ) : isRateLimited ? (
                  <>Retry in {rateLimitCountdown}s</>
                ) : (
                  <>Sign In <ArrowRight size={14} /></>
                )}
              </button>

              <p className="text-center text-xs text-slate-400 pt-1">
                Don&apos;t have an account?{' '}
                <button type="button" onClick={() => switchMode('signup')} className="text-violet-600 hover:text-violet-700 font-medium transition-colors">
                  Create one free
                </button>
              </p>
            </form>
          )}

          {/* ── Signup Form ── */}
          {mode === 'signup' && !signupVerificationSent && (
            <form onSubmit={handleSignupSubmit} noValidate className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Full Name</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="Your full name"
                    autoComplete="name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {signupPassword.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={`strength-${i}`}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                            i <= passwordStrength.score ? passwordStrength.color : 'bg-slate-200'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-400">
                        {passwordStrength.score <= 1 && 'Add uppercase letters, numbers, and symbols'}
                        {passwordStrength.score === 2 && 'Add numbers or symbols to strengthen'}
                        {passwordStrength.score === 3 && 'Good — add symbols for maximum strength'}
                        {passwordStrength.score >= 4 && (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <CheckCircle size={10} /> Strong password
                          </span>
                        )}
                      </p>
                      {passwordStrength.label && (
                        <span className={`text-xs font-semibold ${
                          passwordStrength.score <= 1 ? 'text-rose-500' :
                          passwordStrength.score === 2 ? 'text-amber-500' :
                          passwordStrength.score === 3 ? 'text-violet-600' : 'text-emerald-600'
                        }`}>
                          {passwordStrength.label}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={signupConfirm}
                    onChange={(e) => setSignupConfirm(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-50 border border-violet-100">
                <Info size={13} className="text-violet-500 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  We&apos;ll send a verification link to your email. You&apos;ll need to verify before signing in.
                </p>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={signupTerms}
                  onChange={(e) => setSignupTerms(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 accent-violet-600"
                />
                <label htmlFor="agreeToTerms" className="text-xs text-slate-500 cursor-pointer leading-relaxed">
                  I agree to the{' '}
                  <a href="/terms-and-conditions" className="text-violet-600 hover:text-violet-700 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy-policy" className="text-violet-600 hover:text-violet-700 hover:underline">Privacy Policy</a>
                </label>
              </div>

              {signupError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200">
                  <Shield size={13} className="text-rose-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-rose-700">{signupError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || isRateLimited}
                className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-violet-200"
                style={{ minHeight: '44px' }}
              >
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /><span>Creating account…</span></>
                ) : isRateLimited ? (
                  <>Retry in {rateLimitCountdown}s</>
                ) : (
                  <>Create Account <ArrowRight size={14} /></>
                )}
              </button>

              <p className="text-center text-xs text-slate-400 pt-1">
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} className="text-violet-600 hover:text-violet-700 font-medium transition-colors">
                  Sign in
                </button>
              </p>
            </form>
          )}

          {/* ── Forgot Password Form ── */}
          {mode === 'forgot' && (
            <div>
              {forgotSent ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                    <Mail size={22} className="text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-2">Check your email</p>
                  <p className="text-xs text-slate-500 mb-2">We sent a password reset link to your email address.</p>
                  <p className="text-xs text-slate-400 mb-6">The link expires in 1 hour. Check your spam folder if you don&apos;t see it.</p>
                  <button type="button" onClick={() => { setMode('login'); setForgotSent(false); }} className="text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors">
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} noValidate className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Email Address</label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors"
                        placeholder="you@company.com"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {forgotError && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200">
                      <Shield size={13} className="text-rose-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-rose-700">{forgotError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || isRateLimited}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-violet-200"
                    style={{ minHeight: '44px' }}
                  >
                    {isLoading ? (
                      <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /><span>Sending…</span></>
                    ) : isRateLimited ? (
                      <>Retry in {rateLimitCountdown}s</>
                    ) : (
                      <>Send Reset Link <ArrowRight size={14} /></>
                    )}
                  </button>

                  <button type="button" onClick={() => switchMode('login')} className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-2">
                    ← Back to Sign In
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export default function AuthForm() {
  return <AuthFormInner />;
}
